<template>
  <div :class="$style.topicManager">
    <div :class="$style.header">
      <h3>Manage Topics</h3>
      <button :class="$style.closeButton" @click="closeManager">
        <CloseIcon :size="16" />
      </button>
    </div>

    <div :class="$style.content">
      <!-- Add new topic -->
      <div :class="$style.addSection">
        <h4>Add New Topic</h4>
        <div :class="$style.addForm">
          <input
            v-model="newTopicName"
            type="text"
            placeholder="Enter topic name"
            @keyup.enter="addTopic"
            :class="$style.topicInput"
          />
          <button
            @click="addTopic"
            :class="$style.addButton"
            :disabled="!newTopicName.trim()"
          >
            Add
          </button>
        </div>
      </div>

      <div :class="$style.separator"></div>

      <!-- Active topics -->
      <div :class="$style.topicsSection">
        <h4>Active Topics</h4>
        <div :class="$style.topicsList">
          <div
            v-for="topic in activeTopics"
            :key="topic.id"
            :class="$style.topicItem"
          >
            <div v-if="editingTopicId === topic.id" :class="$style.editForm">
              <input
                v-model="editingTopicName"
                type="text"
                @keyup.enter="saveTopicEdit"
                @blur="saveTopicEdit"
                :class="$style.topicInput"
                ref="editInput"
              />
              <button @click="saveTopicEdit" :class="$style.saveButton">
                Save
              </button>
              <button @click="cancelEdit" :class="$style.cancelButton">
                Cancel
              </button>
            </div>
            <div v-else :class="$style.topicDisplay">
              <span
                :class="[
                  $style.topicName,
                  { [$style.systemTopic]: topic.system },
                ]"
              >
                {{ topic.name }}
                <span v-if="topic.system" :class="$style.systemBadge"
                  >System</span
                >
              </span>
              <div :class="$style.topicActions">
                <button
                  v-if="!topic.system"
                  @click="startEdit(topic)"
                  :class="$style.actionButton"
                  title="Rename"
                >
                  <EditIcon :size="14" />
                </button>
                <button
                  v-if="!topic.system"
                  @click="softDeleteTopic(topic.id)"
                  :class="$style.actionButton"
                  title="Delete"
                >
                  <DeleteIcon :size="14" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Deleted topics (if any) -->
      <div v-if="deletedTopics.length > 0" :class="$style.deletedSection">
        <div :class="$style.separator"></div>
        <h4>Deleted Topics</h4>
        <div :class="$style.topicsList">
          <div
            v-for="topic in deletedTopics"
            :key="topic.id"
            :class="$style.topicItem"
          >
            <div :class="$style.topicDisplay">
              <span :class="[$style.topicName, $style.deletedTopic]">{{
                topic.name
              }}</span>
              <div :class="$style.topicActions">
                <button
                  @click="restoreTopic(topic.id)"
                  :class="$style.actionButton"
                  title="Restore"
                >
                  <RestoreIcon :size="14" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from "vue";
import { topicService } from "../services/topicService";
import type { TaskTopic } from "../types/task";
import { CloseIcon, EditIcon, RestoreIcon } from "./icons";
import DeleteIcon from "./DeleteIcon.vue";

const emit = defineEmits<{
  (e: "close"): void;
}>();

function closeManager() {
  emit("close");
}

const newTopicName = ref("");
const editingTopicId = ref<string | null>(null);
const editingTopicName = ref("");
const editInput = ref<HTMLInputElement | null>(null);

const allTopics = computed(() => topicService.getAllTopics());
const activeTopics = computed(() =>
  allTopics.value.filter((topic) => !topic.deleted)
);
const deletedTopics = computed(() =>
  allTopics.value.filter((topic) => topic.deleted)
);

function addTopic() {
  const name = newTopicName.value.trim();
  if (name) {
    topicService.createTopic(name);
    newTopicName.value = "";
  }
}

function startEdit(topic: TaskTopic) {
  if (topic.system) {
    alert("System topics cannot be edited.");
    return;
  }
  editingTopicId.value = topic.id;
  editingTopicName.value = topic.name;
  nextTick(() => {
    editInput.value?.focus();
    editInput.value?.select();
  });
}

function saveTopicEdit() {
  const name = editingTopicName.value.trim();
  if (name && editingTopicId.value) {
    topicService.updateTopic(editingTopicId.value, name);
    cancelEdit();
  }
}

function cancelEdit() {
  editingTopicId.value = null;
  editingTopicName.value = "";
}

function softDeleteTopic(topicId: string) {
  const topic = topicService.getTopicById(topicId);
  if (topic?.system) {
    alert("System topics cannot be deleted.");
    return;
  }

  if (
    confirm(
      "Are you sure you want to delete this topic? This will hide it from the topic list."
    )
  ) {
    topicService.softDeleteTopic(topicId);
  }
}

function restoreTopic(topicId: string) {
  topicService.restoreTopic(topicId);
}
</script>

<style module>
.topicManager {
  background: #2c3e50;
  border-radius: 8px;
  max-height: 80vh;
  overflow-y: auto;
  color: white;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
}

.header h3 {
  margin: 0;
  font-size: 1.2em;
}

.closeButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.closeButton:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.content {
  padding: 0 20px 20px 20px;
}

.addSection,
.topicsSection,
.deletedSection {
  margin-bottom: 20px;
}

.addSection h4,
.topicsSection h4,
.deletedSection h4 {
  margin: 0 0 12px 0;
  font-size: 1em;
  color: rgba(255, 255, 255, 0.9);
}

.addForm {
  display: flex;
  gap: 8px;
}

.topicInput {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 8px 12px;
  color: white;
  font-size: 14px;
  font-family: inherit;
}

.topicInput:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.topicInput::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.addButton,
.saveButton,
.cancelButton {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.addButton,
.saveButton {
  background: var(--primary-color);
  color: white;
}

.addButton:hover:not(:disabled),
.saveButton:hover {
  background: var(--primary-hover);
}

.addButton:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}

.cancelButton {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.cancelButton:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.separator {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 20px 0;
}

.topicsList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.topicItem {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
  border-left: 3px solid var(--primary-color);
}

.topicDisplay {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.topicName {
  font-weight: 500;
  color: white;
}

.deletedTopic {
  color: rgba(255, 255, 255, 0.5);
  text-decoration: line-through;
}

.systemTopic {
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
}

.systemBadge {
  background: var(--primary-color);
  color: #1a1a1a;
  font-size: 0.7em;
  padding: 2px 6px;
  border-radius: 8px;
  margin-left: 8px;
  font-style: normal;
  font-weight: 600;
}

.topicActions {
  display: flex;
  gap: 4px;
}

.actionButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.actionButton:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.editForm {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .header {
    padding: 15px 15px 0 15px;
  }

  .content {
    padding: 0 15px 15px 15px;
  }

  .addForm {
    flex-direction: column;
  }

  .editForm {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
